import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  SvgIcon,
  InputAdornment,
  TextField,
  Divider,
  DialogContent,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { NewJobSchema, NewCompanyJobSchema } from 'models/NewJobSchema';
import TypeAhead from 'components/TypeAhead';
import { endpoints } from 'utils/axios';
import FormProvider from 'hooks/form/form-provider';
import AddSkills from '../basic-profile/AddSkills';
import { useAuth } from 'hooks/useAuthContext';
import { useSnackbar } from 'components/snackbar';
import { createJob, jobUpdated, getCompanyDetails, jobDetails } from 'api/job';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ScreeningQuestionDrawer from './ScreeningQuestionDrawer';
import { changeNameFormat } from 'utils/Utils';
import { cleanData, clusteredData } from 'data/city.js';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CopyUrlDialog from './CopyUrlDialog';
const backIcon = `${process.env.REACT_APP_HOMEPAGE}assets/arrow_back.svg`;
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';

const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: 6,
  width: 20,
  height: 20,
  boxShadow: 'inset 0 0 0 1.5px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: '#ebf1f5',
    ...theme.applyStyles('dark', {
      backgroundColor: '#30404d',
    }),
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: 'rgba(206,217,224,.5)',
    ...theme.applyStyles('dark', {
      background: 'rgba(57,75,89,.5)',
    }),
  },
  ...theme.applyStyles('dark', {
    boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
    backgroundColor: '#394b59',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
  }),
}));
const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#137cbd',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&::before': {
    display: 'block',
    width: 20,
    height: 20,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});

/**
 * A checkbox component with a "Blueprint" style.
 * @param {object} props Properties to be passed to the underlying Checkbox component.
 * @returns {React.ReactElement} A Checkbox element with the Blueprint style.
 */
function BpCheckbox(props) {
  return (
    <Checkbox
      sx={{ '&:hover': { bgcolor: 'transparent' } }}
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      inputProps={{ 'aria-label': 'Checkbox demo' }}
      {...props}
    />
  );
}

const NewJobPosting = () => {
  const backButtonIcon = (
    <Icon className="extLoginIcon">
      <img alt="edit" width="24px" height="24px" src={backIcon} />
    </Icon>
  );

  const [alignment, setAlignment] = React.useState('tj-jobs');
  const navigate = useNavigate();

  const [selectedChips, setSelectedChips] = useState([]);
  const [loading, setLoading] = useState(true);

  const [jobUniqueId, setJobUniqueId] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [open, setOpen] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const location = useLocation();
  const id = location.state;
  const isEditMode = !!id;
  const [addMoreSkills, setAddMoreSkills] = useState([]);
  const [chipData, setChipData] = useState([]);

  const focusRef = useRef(null);

  const quillRef = useRef(null);
  const compensationTypeRef = useRef(null);
  const [openCopyUrlDialog, setOpenCopyUrlDialog] = useState(false);
  const [posting, setPosting] = useState(false);
  const jobType = [{ label: 'Full time' }, { label: 'Part time' }, { label: 'Internship' }, { label: 'Temporary' }];
  const preferredJobShifts = ['Any', 'Daytime', 'Evening', 'Night'];
  const workModelFormat = ['In office', 'Work from Home', 'Hybrid'];
  const [industryList, setIndustryList] = useState([]);
  const [jobPreference, setJobPreference] = useState('Full time');

  const [openCUEducDrawer, setOpenCUEducDrawer] = React.useState(false);
  const [keyQuestions, setKeyQuestions] = useState([]);

  const [jobData, setJobData] = useState({});
  const [showFlag, setShowFlag] = useState('');
  const [companyLoading, setCompanyLoading] = useState(false);
  const [errorDescription, setErrorDescription] = useState(false);
  const [errorCIN, setErrorCIN] = useState(false);
  const [coreSkill, setCoreSkills] = useState([]);

  const [degreeData, setDegreeData] = useState([]);
  const [certificateTitleData, setCertificateTitleData] = useState([]);
  const [roleData, setRoleData] = useState([]);
  const [drawerJobData, setDrawerJobData] = useState(null);
  const [isEdit, setIsEdit] = React.useState(false);

  const fieldsOrder = [
    "company",
    "company_identification_number",
    "industry_sector",
    "company_website",
    "company_overview",
    "title",
    "job_overview",
    "role",
    "function",
    "location",
    "direct_reports",
    "description",
    "qualifications",
    "min",
    "max",
    "salary",
    "compensation",
    "skills",
  ];

  const defaultValues = {
    company: '',
    company_identification_number: '',
    industry_sector: '',
    company_website: '',
    company_overview: '',
    title: '',
    job_overview: '',
    role: '',
    function: '',
    job_type: 'Full time',
    job_shift: 'Daytime',
    work_mode: 'In office',
    location: '',
    direct_reports: 'Individual contributor (IC)',
    description: '',
    qualifications: [],
    certifications: [],
    min: '',
    max: '',
    experience: '',
    notice_period: 'No preference',
    salary: null,
    compensation: 'Fixed + Variable',
    key_skills: [],
    selectCompany: null,
    start_date: null,
    reporting_to: '',
    gender_preference: 'none',
    disability_preference: 'Not applicable',
    question: [],
    status: 'active',
    company_id: '',
    external_link: '',
    job_responsibilities: '',
    is_company_name_confidential: false,
    is_compensation_confidential: false,
    all_skills: [],
    other_location: [],
    compensation_model: 'Fixed Fee',
    total_time_commitment_requirement: null,
    industry_sector: "Business Services",
    weekly_time_commitment: null
  };

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(
      user?.user_type === 'company' || user?.user_type === 'companyadmin' || user?.user_type === 'recruiter'
        ? NewCompanyJobSchema(alignment)
        : NewJobSchema(alignment, jobPreference)
    ),
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    getValues,
    setError,
    clearErrors,
    reset,
    formState: { errors, isDirty, isValid, dirtyFields, isSubmitting },
    setFocus
  } = methods;
  const min = watch('min');
  const max = watch('max');

  useLayoutEffect(() => {
    if (!isEditMode) focusRef.current?.focus()
  }, []);

  useEffect(() => {
    /**
     * Fetches data required for the new job posting form.
     * Fetches degree titles, certificate titles, and role titles from API.
     * Used in the useEffect hook to fetch data when the component mounts.
     */
    const fetchData = async () => {
      await fetchDegreeData();
      await fetchCertificateTitle();
      await fetchRoleData();
    }
    fetchData();
  }, []);
  useEffect(() => {
    /**
     * Fetches job data from API if in edit mode, otherwise creates a new job.
     * Shows a snackbar error if fetching data fails.
     * Sets loading state to false when done.
     */
    const fetchData = async () => {
      try {
        if (isEditMode) {
          await fetchJobDetails(id);
        } else {
          await createNewJob();
        }
      } catch (error) {
        enqueueSnackbar('Failed to fetch data', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  useEffect(() => {
    if (min !== "" && max !== "") {
      setValue('experience', `${min}-${max}`);
    }
  }, [min, max, setValue]);
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstErrorField = fieldsOrder?.find((field) => errors[field]);
      if (firstErrorField) {
        if (firstErrorField === "description") {
          if (quillRef.current) {
            const quillRefInstance = quillRef.current.getEditor();
            quillRefInstance.focus();
            quillRefInstance.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else if (firstErrorField === "compensation") {
          if (compensationTypeRef.current) {
            compensationTypeRef.current.focus();
            compensationTypeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }
  }, [errors, setFocus]);

  useEffect(() => {
    /**
     * Fetch the list of industries from the server
     * @returns {Promise<void>}
     * @throws {Error} if unable to fetch the industry list
     */
    const fetchIndustryList = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.industry}`);
        const data = await response.json();
        setIndustryList(data || []);
      } catch (error) {
        console.error("Failed to fetch industry list", error);
      }
    };

    fetchIndustryList();
  }, []);

  useEffect(() => {
    if (industryList.length > 0 && !defaultValues?.organization_details?.industry_sector) {
      setValue("organization_details.industry_sector", industryList[0].name);
    }
  }, [industryList]);

  useEffect(() => {
    if (watch('work_mode') === 'Work from Home') setValue("location", 'Remote', { shouldDirty: true });
  }, [watch('work_mode')]);

  /**
   * Updates the core skills and selected chip data with the new set of skills provided.
   * Also validates the skills and filters out any skills that are not in the new set.
   * @param {Array<string>} moreSkills - The new set of skills to add to the core skills and selected chip data.
   */
  const handleAddMoreSkills = (moreSkills) => {
    if (moreSkills?.length > 0) {
      setCoreSkills((coreSkill || []).filter((skill) => (moreSkills || []).includes(skill)));
      setSelectedChips(moreSkills);
      setChipData(moreSkills);
      validateSkills(moreSkills);
    }
  };
  /**
   * Opens the OTP verification dialog.
   */
  const handleOpenOtpDialog = () => {
    setOpen(true);
  };
  /**
   * Closes the OTP verification dialog.
   */
  const handleClose = () => {
    setOpen(false);
  };
  /**
   * Creates a new job and sets the job id and unique id in the state based on the response.
   * If the user is a company, company admin, or recruiter, it also sets the company name, id, industry sector,
   * and company overview in the state.
   * @returns {Promise<void>}
   * @throws {Error} if unable to create the job or fetch the company details
   */
  const createNewJob = async () => {
    try {
      const response = await createJob({ created_by: 'Admin Name', status: 'New' });
      if (response?.success && response?.data) {
        setJobUniqueId(response.data?._id);
        setJobId(response.data?.jobid);
        // setCreatedBy(user?.name);
        // setUpdatedAt(response.data?.updatedAt);
        if (user?.user_type === 'company' || user?.user_type === 'companyadmin' || user?.user_type === 'recruiter') {
          const companyId = user?.id;
          // setValue('selectCompany', { name: user?.name, id: companyId });
          const companyResponse = await getCompanyDetails({ id: companyId });
          if (companyResponse?.success && companyResponse?.data) {
            const companyData = companyResponse.data;
            setValue('company', companyData.full_name);
            setValue('company_id', companyData._id);
            setValue('industry_sector', companyData.industry_sector);
            setValue('company_overview', companyData.description || '');
          } else {
            enqueueSnackbar(companyResponse.message, { variant: 'error' });
          }
        }
      } else {
        enqueueSnackbar('Failed to create job', { variant: 'error' });
      }
    } catch (error) {
      throw error;
    }
  };

  /**
   * Fetches the job details based on the job id provided.
   * Updates the job data state with the fetched job details.
   * If the job has an external link, it sets the alignment to 'external-job-posting'.
   * If the job has a question, it sets the key questions and all certifications in the state.
   * If the job has key skills, it sets the selected chips and core skills in the state.
   * If the job has an experience range, it sets the min and max experience in the state.
   * If the job has all skills, it sets the selected chips and all skills in the state.
   * If the job has a job preference, it sets the job preference in the state.
   * If there is an error, it displays an error snackbar with the message.
   * @param {string} id - The id of the job to fetch the details for.
   * @returns {Promise<void>}
   */
  const fetchJobDetails = async (id) => {
    try {
      const response = await jobDetails({ id });
      if (response?.success && response?.data) {
        const {
          _id,
          jobid,
          question = [],
          key_skills = [],
          experience = '',
          all_skills = [],
          core_skills = [],
          external_link,
          job_type
        } = response?.data;
        setJobData(response.data);
        if (external_link) setAlignment('external-job-posting');
        setJobUniqueId(_id);
        setJobId(jobid);
        reset(response?.data);
        if (question?.length > 0) {
          setKeyQuestions([...question]);
          setValue('question', question);
        }
        setChipData(key_skills?.length > 0 ? key_skills : all_skills);
        setSelectedChips(key_skills);
        setCoreSkills(core_skills);
        if (experience?.includes('-')) {
          const [min, max] = experience.split('-').map(Number);
          setValue('min', min);
          setValue('max', max);
        }
        setValue('all_skills', all_skills);
        setAddMoreSkills(all_skills);
        console.log(job_type);
        setJobPreference(job_type);
      } else {
        enqueueSnackbar(response?.message || 'Failed to fetch job details', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
    }
  };

  const onSubmit = handleSubmit(async (formData) => {
    if (!validateSkills(selectedChips)) {
      return;
    } else {
      setShowFlag('');
    }
    if (!validateDescription(formData?.description)) {
      return;
    }
    if (!validateCIN(formData?.company_identification_number)) {
      return;
    }
    formData.key_skills = selectedChips;
    formData.core_skills = coreSkill;
    formData.description = removeEmptyListItems(formData?.description);

    // setJobData(formData);
    setDrawerJobData(formData);
    if (alignment !== 'external-job-posting') setOpenCUEducDrawer(true);
    else await submitJobApplication(formData);
    setPosting(false);
  });

  /**
   * A higher-order function that takes a boolean and returns a function that
   * toggles the state of the screening questions drawer to that boolean value.
   * @param {boolean} newOpen The new state of the drawer.
   * @returns {function} A function that toggles the state of the drawer.
   */
  const screeningQueDrawer = (newOpen) => () => {
    setOpenCUEducDrawer(newOpen);
  };

  /**
   * Updates the key questions and form value for questions.
   * 
   * @param {Array} qus - The array of questions to set as key questions.
   * This function sets the provided questions as the key questions and updates
   * the form field 'question' with the same array.
   */

  const handleQuestions = (qus) => {
    setKeyQuestions(qus);
    setValue('question', qus);
  };

  /**
   * Handles the chip click event.
   * If the chip is already selected, it removes the chip from the selected skills.
   * If the chip is not selected and the total number of selected chips is less than 10, it adds the chip to the selected skills.
   * If the total number of selected chips is 10, it shows a message to the user saying that they can only select up to 10 skills.
   * @param {string} label - The label of the chip that was clicked.
   */
  const handleClick = (label) => {
    let totalSkillls;
    if (selectedChips?.includes(label)) {
      totalSkillls = selectedChips.filter((chip) => chip !== label);
      setSelectedChips(totalSkillls);
      let filterCoreSkill = coreSkill.filter((chip) => chip !== label);
      setCoreSkills(filterCoreSkill || []);
    } else {
      if (selectedChips?.length >= 10) {
        setShowFlag('You can select up to 10 skills.');
        return;
      } else {
        totalSkillls = [...selectedChips, label];
        setSelectedChips(totalSkillls);
        setShowFlag('');
      }
    }
    validateSkills(totalSkillls);
  };


  /**
   * Handles changes to the ToggleButtonGroup.
   * When the toggle value changes, it updates the state with the new alignment.
   * If the new alignment is 'external-job-posting', it sets the form field 'compensation_model' to 'Fixed Fee' and the form field 'external_link' to an empty string.
   * If the new alignment is not 'external-job-posting', it filters the key questions to remove any empty strings.
   */
  const handleToggleChange = (event, newAlignment) => {
    if (newAlignment !== null) setAlignment(newAlignment);

    if (!(newAlignment === 'external-job-posting')) {
      setValue("compensation_model", 'Fixed Fee');
      setValue("external_link", '');
    } else {
      setKeyQuestions(keyQuestions.filter(q => q.trim() !== ''));
    }
  };

  /**
   * Resets the form to its default values and clears out the chips and skills.
   */
  const onReset = () => {
    reset(defaultValues);
    setKeyQuestions(['']);
    setSelectedChips([]);
    setAddMoreSkills([]);
    setCoreSkills([]);
    setChipData([]);
  };

  /**
   * Saves the current job posting as a draft.
   * @returns {Promise<void>}
   * @fires jobUpdated
   * @listens enqueueSnackbar
   * @listens navigate
   */
  const saveAsDraft = async () => {
    const data = watch();
    // console.log(data?.salary);
    data.key_skills = selectedChips;
    data.core_skills = coreSkill;
    const filledData = Object.fromEntries(Object.entries(data).filter(([_, value]) => value !== '' && value !== null));
    filledData.status = 'draft';
    const response = await jobUpdated({ formData: filledData, job_id: jobUniqueId });
    if (response?.success === true && response?.data) {
      enqueueSnackbar('Job draft saved successfully', { variant: 'success' });
      navigate('/home');
    } else if (response?.success === false) {
      enqueueSnackbar('Failed to update job', { variant: 'error' });
      console.error('Failed to update job:', response.message);
    }
  };

  /**
   * Checks if the total number of skills is within the valid range of 5-10.
   * @param {number} totalSkillls - The total number of skills.
   * @returns {boolean} True if the number of skills is valid, false otherwise.
   */
  const validateSkills = (totalSkillls) => {
    if (totalSkillls?.length < 5) {
      setShowFlag('Please select at least 5 skills');
      return false;
    } else if (totalSkillls?.length > 10) {
      setShowFlag('You can select up to 10 skills');
      return false;
    }
    setShowFlag('');
    return true;
  };

  /**
   * Fetches the company data given the id and populates the form with the
   * retrieved data.
   * @param {string} id - The company id.
   * @returns {Promise<void>}
   * @listens setValue
   * @listens trigger
   * @listens setCompanyLoading
   * @fires enqueueSnackbar
   * @listens console.error
   */
  const handleCompanyData = async (id) => {
    setCompanyLoading(true);
    try {
      setValue('company_id', id || '');
      const companyResponse = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.singleCompanyData}?id=${id}`);
      const companyData = await companyResponse.json();
      setValue('industry_sector', companyData?.industry_sector || '');
      setValue('company_overview', companyData?.description || '');
      setValue('company_identification_number', companyData?.identification_number || '');
      setValue('company_website', companyData?.website || '');
      if (companyData?.industry_sector) trigger('industry_sector');
      if (companyData?.identification_number) trigger('company_identification_number');
      if (companyData?.description) trigger('company_overview');
      if (companyData?.website) trigger('company_website');
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
    setCompanyLoading(false);
  };
  /**
   * Checks if the given job description is valid.
   * It does this by parsing the html string and checking if the text content of the parsed string is not empty.
   * @param {string} value - The job description as a string.
   * @returns {boolean} True if the job description is valid, false otherwise.
   */
  const validateDescription = (value) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, 'text/html');
    const text = doc.body.textContent || "";
    if (text && text?.length > 0) {
      setErrorDescription(false);
      return true;
    } else {
      setErrorDescription(true);
      return false;
    }
  };
  /**
   * Checks if the given company identification number is valid.
   * It does this by checking if the length of the CIN is 21.
   * @param {string} value - The company identification number as a string.
   * @returns {boolean} True if the CIN is valid, false otherwise.
   */
  const validateCIN = (value) => {
    if (!value) {
      setErrorCIN(false);
      return true;
    }
    if (value && value?.length === 21) {
      setErrorCIN(false);
      return true;
    } else {
      setErrorCIN(true);
      return false;
    }
  };
  /**
   * Updates the 'other_location' field based on the specified location by matching it
   * against the clustered data and extracting the corresponding names.
   * 
   * @param {Object} location - The location object containing a 'value' property to match.
   */

  const handleOtherLocation = (location) => {
    const matchedClusterData = clusteredData.filter(cluster => cluster?.value?.includes(location));
    const otherLocationName = matchedClusterData?.map(cluster => cluster?.name);
    setValue('other_location', otherLocationName);
  };
  /**
   * Handles the selection of core skills. If the label is already selected, it
   * removes it from the list of core skills. If the label is not selected and the
   * length of the core skills list is less than 5, it adds the label to the list.
   * If the length of the core skills list is 5 or more, it shows an error message.
   * @param {string} label - The label of the core skill to be selected or deselected.
   */
  const handleCoreSkills = (label) => {
    let totalSkillls;
    if (coreSkill?.includes(label)) {
      totalSkillls = coreSkill.filter((chip) => chip !== label);
      setCoreSkills(totalSkillls);
      setShowFlag('');
    } else {
      if (coreSkill?.length >= 5) {
        setShowFlag('You can select up to 5 core skills.');
        return;
      } else {
        totalSkillls = [...coreSkill, label];
        setCoreSkills(totalSkillls);
        setShowFlag('');
      }
    }
  };

  /**
   * Submits the new job application to the server.
   * @param {Object} formData - The form data containing the job details.
   * @returns {Promise<void>} - Resolves when the job is created successfully, rejects if there's an error.
   */
  const submitJobApplication = async (formData) => {
    setPosting(true);
    formData.status = 'active';
    formData.question = [];
    try {
      const response = await jobUpdated({ formData, job_id: jobUniqueId });
      if (response?.success === true && response.data) {
        enqueueSnackbar(response.message || 'New job created successfully', { variant: 'success' });
        setOpenCopyUrlDialog(true);
      } else if (response?.success === false) {
        enqueueSnackbar('Failed to create job', { variant: 'error' });
        console.error('Failed to create job:', response.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPosting(false);
    }
  };
  /**
   * Closes the copy url dialog and navigates the user to the home page.
   * @memberof NewJobPosting
   */
  const handleCloseCopyUrlDialog = () => {
    setOpenCopyUrlDialog(false);
    navigate('/home');
  };
  /**
   * Fetches the degree data from the server and sets the degree data state to the fetched data.
   * The degree data is deduplicated using a Set.
   * @memberof NewJobPosting
   * @returns {Promise<void>} - Resolves when the degrees are fetched successfully, rejects if there's an error.
   */
  const fetchDegreeData = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.degrees}`);
    const resultData = await response.json();
    setDegreeData([...new Set([...resultData])]);
  };
  /**
   * Fetches certificate titles from the server and updates the certificate title data state.
   * The certificate titles are deduplicated using a Set.
   * @memberof NewJobPosting
   * @returns {Promise<void>} - Resolves when the certificate titles are fetched successfully, rejects if there's an error.
   */

  const fetchCertificateTitle = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.certificate}`);
    const resultData = await response.json();
    setCertificateTitleData([...new Set([...resultData])]);
  };
  /**
   * Fetches the roles from the server and updates the role data state.
   * The roles are deduplicated using a Set.
   * @memberof NewJobPosting
   * @returns {Promise<void>} - Resolves when the roles are fetched successfully, rejects if there's an error.
   */
  const fetchRoleData = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.roles}`);
    const resultData = await response.json();
    setRoleData([...new Set([...resultData])]);
  };
  /**
   * Removes empty list items from the given HTML string.
   * @param {string} value - The HTML string to remove empty list items from.
   * @returns {string} - The updated HTML string with no empty list items.
   */
  const removeEmptyListItems = (value) => {
    const div = document.createElement('div');
    div.innerHTML = value;
    const listItems = div.querySelectorAll('li');
    listItems.forEach((item) => {
      if (!item.textContent.trim()) {
        item.remove();
      }
    });
    return div.innerHTML;
  };

  /**
   * Handles changes to the job type selection.
   * Updates various form fields and preferences based on the selected job type.
   * If the job type is not 'Full time', sets default values for the notice period
   * and compensation. If the job type is 'Full time', clears start date and 
   * time commitment fields, and sets a fixed fee compensation model.
   * 
   * @param {Object} e - The event object containing the target value of the selected job type.
   */

  const handleJobTypeChange = (e) => {
    const updatedValue = e.target.value;
    setValue("job_type", updatedValue, { shouldDirty: true });
    trigger('job_type');
    setJobPreference(updatedValue);
    setValue("salary", null);
    setValue("is_compensation_confidential", false);

    if (updatedValue !== 'Full time') {
      setValue("notice_period", 'No preference');
      setValue("compensation", 'Fixed + Variable');
    } else {
      setValue("start_date", null);
      setValue("weekly_time_commitment", null);
      setValue("total_time_commitment_requirement", null);
      setValue("compensation_model", 'Fixed Fee');
    }
  };

  const canSaveAsDraft = () => {
    const fields = getValues([
      'company',
      'industry_sector',
      'company_website',
      'company_overview',
      'role',
      'function'
    ]);
    return fields.every((field) => field && field.trim() !== '');
  };

  const isDraftEnabled = canSaveAsDraft();


  if (loading || !degreeData || !degreeData?.length > 0 || !certificateTitleData || !certificateTitleData?.length > 0 || !roleData || !roleData?.length > 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '60px', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  };

  return (
    <Box>
      {/* <Header /> */}
      <Box className="admin-new-job-posting">
        <Container maxWidth="xl">
          <Box sx={{ padding: '5rem 1.5rem' }}>
            {/* <Link href="#" className="update-link" underline="none" onClick={createUpdateEduDrawer(true)}>
              open side panel
            </Link> */}
            <Box mb={1}>
              <Button size="small" className="back-btn" startIcon={backButtonIcon} onClick={() => { navigate(`/home`, { state: id }) }}>
                Back to Job Listing
              </Button>
            </Box>

            {loading ?
              <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '30px', alignItems: 'center' }}>
                <CircularProgress />
              </Box> :
              <Box>
                <Box mb={4}>
                  <Typography className="page-title" mb={3}>
                    New Job Posting
                  </Typography>
                  <Box mb={2} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <ToggleButtonGroup
                      className="job-post-toggle-btn"
                      color="primary"
                      value={alignment}
                      exclusive
                      onChange={handleToggleChange}
                      aria-label="Platform"
                      sx={{ gap: '20px' }}
                    >
                      <ToggleButton value="tj-jobs">TJ Jobs</ToggleButton>
                      <ToggleButton value="external-job-posting" variant="outlined">External Job Posting</ToggleButton>
                    </ToggleButtonGroup>

                  </Box>
                </Box>
                <FormProvider methods={methods} onSubmit={onSubmit}>
                  {/* Company Details */}
                  <Box mb={4}>
                    <Box className="card-container">
                      <Box className="card-title">
                        <Typography className="title-text">Recruitment Company Details</Typography>
                      </Box>

                      {companyLoading ?
                        <Box className="card-body">
                          <Stack sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '4px', height: '210px' }}>
                            <CircularProgress size={24} />
                          </Stack>
                        </Box> :
                        <Box className="card-body">
                          <Grid container spacing={4} mb={2}>
                            <Grid item xs={6}>
                              {/* company */}
                              <FormControl fullWidth>
                                {user?.user_type == 'admin' || user?.user_type == 'superadmin' && (
                                  <Controller
                                    control={control}
                                    name="company"
                                    defaultValue={defaultValues?.company || ''}
                                    render={({ field }) =>
                                      <TypeAhead
                                        {...field}
                                        {...register('company')}
                                        api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.company}`}
                                        multiple={false}
                                        fullWidth
                                        label="Company Name"
                                        displayTextKey="name"
                                        placeholder="Company Name"
                                        freeSolo={true}
                                        onInputChange={(e, value) => {
                                          setValue("company", value, { shouldDirty: true });
                                        }}
                                        onChange={(e, value) => {
                                          if (value?.name) {
                                            handleCompanyData(value?._id);
                                            setValue("company", value?.name, { shouldDirty: true });
                                            setValue("company", value);
                                          } else {
                                            setValue("company", {});
                                            setValue('industry_sector', '');
                                            setValue('company_overview', '');
                                            setValue('company_identification_number', '');
                                            setValue('company_website', '');
                                            setValue('company_id', '');
                                          }
                                        }}
                                        inputProps={{
                                          style: { textTransform: 'capitalize' }
                                        }}
                                        error={!!errors.company}
                                        helperText={errors.company?.message}
                                      />
                                    }
                                  />
                                )}
                              </FormControl>
                              {/* is company name confidential */}
                              <Controller
                                name="is_company_name_confidential"
                                control={control}
                                render={({ field }) => (
                                  <FormControlLabel
                                    control={
                                      <BpCheckbox
                                        {...field}
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        sx={{ color: 'rgba(0, 0, 0, 0.38)', '&.Mui-checked': { color: '#1976d2', } }}
                                      />
                                    }
                                    label="Keep company name confidential"
                                    sx={{
                                      '& .MuiFormControlLabel-label': {
                                        fontWeight: 'normal',
                                        color: 'rgba(0, 0, 0, 0.54)',
                                      }
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              {/* company identification number */}
                              <FormControl fullWidth>
                                <Controller
                                  name="company_identification_number"
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      {...register('company_identification_number')}
                                      size="small"
                                      error={!!errors.company_identification_number}
                                      helperText={errors.company_identification_number?.message}
                                      label="Company Identification Number (CIN)"
                                      onChange={(e) => {
                                        field.onChange(e.target.value);
                                        validateCIN(e.target.value);
                                      }}
                                      variant="outlined"
                                      fullWidth
                                      multiline
                                    />
                                  )}
                                />
                                {errorCIN && <FormHelperText error>Please enter a 21-character CIN.</FormHelperText>}
                              </FormControl>
                            </Grid>
                          </Grid>

                          <Grid container spacing={4} mb={3}>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small" error={!!errors.industry_sector}>
                                <Controller
                                  control={control}
                                  name="industry_sector"
                                  defaultValue={defaultValues?.industry_sector || ''}
                                  render={({ field }) => (
                                    <Autocomplete
                                      options={industryList}
                                      getOptionLabel={(option) => option.name || ''}
                                      isOptionEqualToValue={(option, value) => option._id === value._id}
                                      value={industryList.find(item => (item.name).toLowerCase() === (field.value).toLowerCase()) || null}
                                      onChange={(event, newValue) => {
                                        field.onChange(newValue ? newValue.name : '');
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Industry Sector"
                                          size="small"
                                          error={!!errors?.industry_sector}
                                        />
                                      )}
                                      fullWidth
                                    />
                                  )}
                                />
                                {errors.industry_sector && (
                                  <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                    {errors.industry_sector.message}
                                  </Typography>
                                )}
                              </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                              {/* company website */}
                              <FormControl fullWidth>
                                <Controller
                                  name="company_website"
                                  control={control}
                                  defaultValue={defaultValues?.company_website || ''}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      {...register('company_website')}
                                      size="small"
                                      error={!!errors.company_website}
                                      helperText={errors.company_website?.message}
                                      label="Company Website"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                          {/* company overview */}
                          <FormControl fullWidth>
                            <Controller
                              name="company_overview"
                              control={control}
                              defaultValue={defaultValues?.company_overview}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  {...register('company_overview')}
                                  size="small"
                                  label="Company Overview"
                                  variant="outlined"
                                  fullWidth
                                  multiline
                                  error={!!errors.company_overview}
                                  helperText={errors.company_overview?.message || `${getValues('company_overview') ? getValues('company_overview')?.trim()?.split(/\s+/)?.length || 0 : 0} / 30 words`}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    const words = inputValue.trim().split(/\s+/);
                                    if (words?.length <= 30) {
                                      setValue('company_overview', inputValue);
                                      clearErrors('company_overview');
                                    } else {
                                      setError('company_overview', { message: "Maximum 30 words allowed." });
                                    }
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                        </Box>
                      }
                    </Box>
                  </Box>

                  {/* Job Details */}
                  <Box mb={4}>
                    <Box className="card-container">
                      <Box className="card-title">
                        <Typography className="title-text">Job Details</Typography>
                      </Box>

                      <Box className="card-body">
                        {/* job overview */}
                        <FormControl fullWidth sx={{ marginBottom: '1rem' }}>
                          <Controller
                            name="job_overview"
                            control={control}
                            defaultValue={defaultValues?.job_overview}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                size="small"
                                {...register('job_overview')}
                                error={!!errors.job_overview}
                                helperText={errors.job_overview?.message}
                                label="Job Summary (Optional)"
                                variant="outlined"
                                fullWidth
                                multiline
                              />
                            )}
                          />
                          {/* <Typography component="p" className="need-help-txt">
                            Need help with some examples?
                            <Link href="#"> Click Here</Link>
                          </Typography> */}
                        </FormControl>
                        <Box mb={1} />

                        <Grid container spacing={4}>
                          <Grid item xs={6}>
                            {/* role */}
                            <FormControl fullWidth sx={{ marginBottom: '1rem' }}>
                              <Controller
                                control={control}
                                name="role"
                                defaultValue={defaultValues?.role || ''}
                                render={({ field }) => (
                                  <TextField
                                    size="small"
                                    {...field}
                                    {...register('role')}
                                    label="Designation"
                                    variant="outlined"
                                    fullWidth
                                    error={!!errors.role}
                                    helperText={errors.role ? errors.role.message : ''}
                                    onChange={(e) => {
                                      const updatedValue = changeNameFormat(e.target.value);
                                      setValue("role", updatedValue, { shouldDirty: true });
                                      setValue("title", updatedValue, { shouldDirty: true });
                                      trigger('role');
                                    }
                                    }
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            {/* function */}
                            <FormControl fullWidth size="small" sx={{ marginBottom: '1rem' }}>
                              <Controller
                                control={control}
                                name="function"
                                defaultValue={defaultValues?.function || ''}
                                render={({ field }) => (
                                  <TypeAhead
                                    {...field}
                                    {...register('function')}
                                    data={roleData}
                                    label="Job Function"
                                    size="small"
                                    displayTextKey="name"
                                    onChange={(e, value) => {
                                      setValue('all_skills', value?.skills);
                                      field.onChange(value?.name);
                                      setChipData(value?.skills);
                                      setAddMoreSkills(value?.skills);
                                      setSelectedChips([]);
                                      setCoreSkills([]);
                                    }}
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>

                        <Grid container spacing={4}>
                          <Grid item xs={6}>
                            {/* job type */}
                            <FormControl fullWidth size="small" sx={{ marginBottom: '1rem' }} error={!!errors.job_type}>
                              <InputLabel size='small' id="job_type">Job Type</InputLabel>
                              <Controller
                                control={control}
                                name="job_type"
                                defaultValue={defaultValues?.job_type || ''}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    {...register('job_type')}
                                    labelId="job_type"
                                    error={!!errors.job_type}
                                    helperText={errors.job_type?.message}
                                    label="Job Type"
                                    variant="outlined"
                                    fullWidth
                                    onChange={(e) => handleJobTypeChange(e)}
                                  >
                                    {jobType?.map((item) =>
                                      <MenuItem value={item?.label}>{item?.label}</MenuItem>
                                    )}
                                  </Select>
                                )}
                              />
                              <Typography variant="caption" color="error">
                                {errors?.job_type?.message}
                              </Typography>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            {/* job shift */}
                            <FormControl fullWidth size="small" sx={{ marginBottom: '1rem' }} error={!!errors.job_shift}>
                              <InputLabel size="small" id="job_shift">
                                Job Shift
                              </InputLabel>
                              <Controller
                                name="job_shift"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    {...register('job_shift')}
                                    size="small"
                                    label="Job Shift"
                                    variant="outlined" fullWidth>
                                    {preferredJobShifts?.map((item) =>
                                      <MenuItem value={item}>{item}</MenuItem>
                                    )}
                                  </Select>
                                )}
                              />
                              {errors.job_shift && <FormHelperText error>{errors.job_shift?.message}</FormHelperText>}
                            </FormControl>
                            {/* Working Hours */}
                          </Grid>
                        </Grid>
                        <Grid container spacing={4}>
                          <Grid item xs={6}>
                            {/* work mode */}
                            <FormControl fullWidth size="small" sx={{ marginBottom: '1rem' }} error={!!errors.work_mode}>
                              <InputLabel size="small" id="work_mode">
                                Job Format
                              </InputLabel>
                              <Controller
                                control={control}
                                name="work_mode"
                                defaultValue={defaultValues?.work_mode || ''}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    {...register('work_mode')}
                                    labelId="work_mode"
                                    error={!!errors.work_mode}
                                    helperText={errors.work_mode?.message}
                                    label="Job Format"
                                    variant="outlined"
                                    fullWidth
                                  >
                                    {workModelFormat?.map((item) =>
                                      <MenuItem value={item}>{item}</MenuItem>
                                    )}
                                  </Select>
                                )}
                              />
                              {errors?.work_mode && <FormHelperText error>{errors.work_mode?.message}</FormHelperText>}
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            {/* location */}
                            <FormControl fullWidth size="small" sx={{ marginBottom: '1rem' }}>
                              <Controller
                                control={control}
                                name="location"
                                defaultValue={defaultValues?.location || ''}
                                render={({ field }) => (
                                  <TypeAhead
                                    {...field}
                                    {...register('location')}
                                    api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.location}`}
                                    label="Job Location"
                                    freeSolo={true}
                                    onInputChange={(e, value) => {
                                      setValue("location", value, { shouldDirty: true });
                                    }}
                                    onChange={(e, value) => {
                                      setValue("location", value, { shouldDirty: true });
                                      handleOtherLocation(value);
                                    }}
                                    disabled={watch('work_mode') === 'Work from Home' ? true : false}
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>

                        <Grid container columnSpacing={4}>
                          <Grid item xs={6}>
                            {/* direct reports */}
                            <FormControl fullWidth size="small" error={!!errors?.direct_reports}>
                              <InputLabel id="direct_reports">Number of people reporting</InputLabel>
                              <Controller
                                control={control}
                                name="direct_reports"
                                defaultValue={defaultValues?.direct_reports || ''}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    {...register('direct_reports')}
                                    labelId="direct_reports"
                                    label="Number of people reporting"
                                    variant="outlined"
                                    fullWidth
                                  >
                                    <MenuItem value="Individual contributor (IC)">Individual contributor (IC)</MenuItem>
                                    <MenuItem value="1-4">1-4</MenuItem>
                                    <MenuItem value="5-10">5-10</MenuItem>
                                    <MenuItem value="11-25">11-25</MenuItem>
                                    <MenuItem value="26-50">26-50</MenuItem>
                                    <MenuItem value="51-100">51-100</MenuItem>
                                    <MenuItem value="100+">100+</MenuItem>
                                  </Select>
                                )}
                              />
                              {errors?.direct_reports && <FormHelperText error>{errors?.direct_reports?.message}</FormHelperText>}
                            </FormControl>
                          </Grid>

                          {alignment === 'external-job-posting' && (
                            <Grid item xs={6}>
                              {/* external link */}
                              <FormControl fullWidth sx={{ marginBottom: '1rem' }}>
                                <Controller
                                  name="external_link"
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      {...register('external_link')}
                                      size="small"
                                      error={!!errors.external_link}
                                      helperText={errors.external_link?.message}
                                      label="Job Application Link"
                                      variant="outlined"
                                      fullWidth
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Typography className="label-text" mb={1}>Job Description</Typography>
                            {/* description */}
                            <FormControl fullWidth sx={{ marginBottom: '0.5rem' }} className={`quill-container-job ${(errors.description || errorDescription) ? 'error-quill' : ''}`}>
                              <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                  <ReactQuill
                                    {...field}
                                    {...register('description')}
                                    ref={quillRef}
                                    value={field.value}
                                    onChange={(value) => {
                                      setValue('description', value);
                                      validateDescription(value);
                                      trigger('description');
                                    }}
                                    theme="snow"
                                    placeholder="Job Description"
                                    onBlur={field.onBlur}
                                    modules={{
                                      toolbar: [
                                        [{ 'size': ['small', false, 'large', 'huge'] }],
                                        ['bold', 'italic', 'underline'],
                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                                      ]
                                    }}
                                  />
                                )}
                              />
                              {(errors?.description || errorDescription) && <FormHelperText error>Job description is required.</FormHelperText>}
                              <Typography component="p" className="need-help-txt">
                                Need help with some examples?
                                <Link href="#"> Click Here</Link>
                              </Typography>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Box>

                  {/* Job Requirements */}
                  <Box mb={4}>
                    <Box className="card-container">
                      <Box className="card-title">
                        <Typography className="title-text">Job Requirements</Typography>
                      </Box>
                      <Box className="card-body">
                        <Grid container spacing={4}>
                          <Grid item xs={6}>
                            {/* qualifications/education */}
                            <FormControl fullWidth size="small" sx={{ marginBottom: '1rem' }}>
                              <Controller
                                control={control}
                                name="qualifications"
                                defaultValue={defaultValues?.qualifications || []}
                                render={({ field }) => (
                                  <TypeAhead
                                    {...field}
                                    {...register('qualifications')}
                                    data={degreeData}
                                    label="Minimum Educational Qualification (Any of the below can apply)"
                                    displayTextKey="display_name"
                                    multiple={true}
                                    disableClearable={true}
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            {/* certificate */}
                            <FormControl fullWidth size="small">
                              <Controller
                                control={control}
                                name="certifications"
                                defaultValue={defaultValues?.certifications || []}
                                render={({ field }) => (
                                  <TypeAhead
                                    {...field}
                                    {...register('certifications')}
                                    data={certificateTitleData}
                                    multiple={true}
                                    label="Certification"
                                    displayTextKey="name"
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                        <Grid container spacing={4}>
                          <Grid item xs={6}>
                            <Typography className="label-text" mb={1}>
                              Experience Required (in years)
                            </Typography>

                            <Grid container spacing={4}>
                              <Grid item xs={6}>
                                {/* min experience */}
                                <FormControl fullWidth sx={{ marginBottom: '1.5rem' }}>
                                  <Controller
                                    name="min"
                                    control={control}
                                    defaultValue={defaultValues?.min}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        type="number"
                                        label="Minimum"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        error={!!errors.min}
                                        helperText={errors.min ? errors.min.message : ''}
                                      />
                                    )}
                                  />
                                </FormControl>
                              </Grid>
                              <Grid item xs={6}>
                                {/* max experience */}
                                <FormControl fullWidth sx={{ marginBottom: '1.5rem' }}>
                                  <Controller
                                    name="max"
                                    control={control}
                                    defaultValue={defaultValues?.max}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        type="number"
                                        label="Maximum"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        error={!!errors.max}
                                        helperText={errors.max ? errors.max.message : ''}
                                      />
                                    )}
                                  />
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={6} sx={{ marginTop: '27px' }}>
                            {/* start date / notice period */}
                            {jobPreference !== 'Full time' ? (
                              <FormControl fullWidth>
                                <Controller
                                  control={control}
                                  name="start_date"
                                  defaultValue={defaultValues?.start_date ? dayjs(defaultValues.start_date) : null}
                                  render={({ field }) => (
                                    <DatePicker
                                      {...field}
                                      label="Start Date"
                                      format="DD/MM/YYYY"
                                      disablePast
                                      onChange={(date) => field.onChange(date)}
                                      value={field.value ? dayjs(field.value) : null}
                                      slotProps={{
                                        textField: {
                                          size: 'small',
                                          error: !!errors?.start_date,
                                          helperText: errors?.start_date?.message,
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </FormControl>
                            ) : (
                              <FormControl
                                fullWidth
                                size="small"
                                sx={{ marginBottom: '1.5rem' }}
                                error={!!errors.notice_period}
                              >
                                <InputLabel id="notice_period">Maximum Notice Period (in days)</InputLabel>
                                <Controller
                                  name="notice_period"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      label=" Maximum Notice Period (in days)"
                                      size="small"
                                      fullWidth
                                    >
                                      <MenuItem value="No preference">No preference</MenuItem>
                                      <MenuItem value="0 days (immediate)">0 days (immediate)</MenuItem>
                                      <MenuItem value="15">Upto 15 Days</MenuItem>
                                      <MenuItem value="30">Upto 30 Days</MenuItem>
                                      <MenuItem value="45">Upto 45 Days</MenuItem>
                                      <MenuItem value="60">Upto 60 Days</MenuItem>
                                      <MenuItem value="75">Upto 75 Days</MenuItem>
                                      <MenuItem value="90">Upto 90 Days</MenuItem>
                                      <MenuItem value="90+">Upto 90+ Days</MenuItem>
                                    </Select>
                                  )}
                                />
                                {errors.notice_period && (
                                  <FormHelperText>{errors.notice_period.message}</FormHelperText>
                                )}
                              </FormControl>
                            )}
                          </Grid>
                        </Grid>

                        <Grid container spacing={4}>
                          <Grid item xs={6}>
                            {/* Weekly Time Commitment / (salary / is compensation confidential) */}
                            {jobPreference !== 'Full time' ? (
                              <FormControl fullWidth>
                                <Controller
                                  control={control}
                                  name="weekly_time_commitment"
                                  defaultValue={defaultValues?.weekly_time_commitment || ''}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      label="Weekly Time Commitment (hours per week)"
                                      variant="outlined"
                                      size="small"
                                      value={field?.value ? field?.value : ''}
                                      type="number"
                                      error={!!errors.weekly_time_commitment}
                                      helperText={errors.weekly_time_commitment?.message}
                                      onChange={(e) => field.onChange(e.target.value)}
                                    />
                                  )}
                                />
                              </FormControl>
                            ) : (
                              <>
                                <FormControl fullWidth error={!!errors?.salary}>
                                  <Controller
                                    control={control}
                                    name="salary"
                                    defaultValue={defaultValues?.salary || ''}
                                    render={({ field }) =>
                                      <TextField
                                        {...field}
                                        id="salary"
                                        name="salary"
                                        label="Maximum Annual Compensation"
                                        variant="outlined"
                                        size="small"
                                        error={!!errors.salary}
                                        helperText={errors.salary?.message}
                                        type="text"
                                        value={
                                          isEdit
                                            ? field.value
                                            : field.value
                                              ? new Intl.NumberFormat('en-IN').format(field.value)
                                              : ''
                                        }
                                        onFocus={() => setIsEdit(true)}
                                        onBlur={(e) => {
                                          setIsEdit(false);
                                          let rawValue = e.target.value.replace(/,/g, '');
                                          let numValue = parseInt(rawValue, 10);

                                          if (!isNaN(numValue)) {
                                            field.onChange(numValue);
                                          }
                                        }}
                                        onChange={(e) => {
                                          let rawValue = e.target.value.replace(/,/g, '');
                                          if (/^\d*$/.test(rawValue)) {
                                            field.onChange(rawValue);
                                          }
                                        }}
                                        InputProps={{
                                          startAdornment: (
                                            field?.value ? (
                                              <InputAdornment position="start">
                                                <CurrencyRupeeIcon fontSize="small" />
                                              </InputAdornment>) : null
                                          ),
                                        }}
                                      />
                                    }
                                  />
                                </FormControl>
                                <Controller
                                  name="is_compensation_confidential"
                                  control={control}
                                  render={({ field }) => (
                                    <FormControlLabel
                                      control={
                                        <BpCheckbox
                                          {...field}
                                          checked={field.value}
                                          onChange={(e) => field.onChange(e.target.checked)}
                                        />
                                      }
                                      label="Keep compensation confidential"
                                      sx={{
                                        '& .MuiFormControlLabel-label': {
                                          fontWeight: 'normal',
                                          color: 'rgba(0, 0, 0, 0.54)',
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </>
                            )}
                          </Grid>

                          <Grid item xs={6}>
                            {/* Total Time Commitment Requirement */}
                            {/* compensation */}
                            {jobPreference !== 'Full time' ? (
                              <FormControl fullWidth>
                                <Controller
                                  control={control}
                                  name="total_time_commitment_requirement"
                                  defaultValue={defaultValues?.total_time_commitment_requirement || ''}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      label="Total Time Commitment Requirement (weeks)"
                                      variant="outlined"
                                      size="small"
                                      value={field?.value ? field?.value : ''}
                                      type="number"
                                      error={!!errors.total_time_commitment_requirement}
                                      helperText={errors.total_time_commitment_requirement?.message}
                                      onChange={(e) => field.onChange(e.target.value)}
                                    />
                                  )}
                                />
                              </FormControl>
                            ) : (
                              <FormControl fullWidth size="small" error={!!errors.compensation}>
                                <InputLabel size="small">Compensation Type</InputLabel>
                                <Controller
                                  name="compensation"
                                  control={control}
                                  defaultValue={defaultValues?.compensation || ''}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      inputRef={compensationTypeRef}
                                      size="small"
                                      label="Compensation Type"
                                      fullWidth
                                    >
                                      <MenuItem value="All Fixed">All Fixed</MenuItem>
                                      <MenuItem value="Fixed + Variable">Fixed + Variable</MenuItem>
                                      <MenuItem value="Fixed + Variable + Other Perks">
                                        Fixed + Variable + Other Perks
                                      </MenuItem>
                                    </Select>
                                  )}
                                />
                                {errors.compensation && (
                                  <FormHelperText>{errors.compensation?.message}</FormHelperText>
                                )}
                              </FormControl>
                            )}
                          </Grid>
                        </Grid>

                        {jobPreference !== 'Full time' && (
                          <Grid container spacing={4} mt={-2}>
                            <Grid item xs={6}>
                              {/* Compensation Model */}
                              <FormControl fullWidth size="small" error={!!errors.compensation_model}>
                                <InputLabel size="small">Compensation Model</InputLabel>
                                <Controller
                                  name="compensation_model"
                                  control={control}
                                  defaultValue={defaultValues?.compensation_model || ''}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      size="small"
                                      label="Compensation Model"
                                      fullWidth
                                    >
                                      <MenuItem value="Fixed Fee">Fixed Fee</MenuItem>
                                      <MenuItem value="per hour">Hourly</MenuItem>
                                    </Select>
                                  )}
                                />
                                {errors.compensation_model && (
                                  <FormHelperText>{errors.compensation_model.message}</FormHelperText>
                                )}
                              </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                              {/* Total Offered Compensation(salary) */}
                              <FormControl fullWidth error={!!errors?.salary}>
                                <Controller
                                  control={control}
                                  name="salary"
                                  defaultValue={defaultValues?.salary || ''}
                                  render={({ field }) => {
                                    const [isEditing, setIsEditing] = React.useState(false);

                                    const compensationModel = watch('compensation_model');
                                    const salaryLabel =
                                      compensationModel === 'Fixed Fee'
                                        ? 'Maximum Fixed Fee Compensation'
                                        : 'Maximum Hourly Compensation (per hour)';
                                    return (
                                      <TextField
                                        {...field}
                                        id="salary"
                                        name="salary"
                                        label={salaryLabel}
                                        variant="outlined"
                                        size="small"
                                        error={!!errors.salary}
                                        helperText={errors.salary?.message}
                                        type="text"
                                        value={
                                          isEditing
                                            ? field.value
                                            : field.value
                                              ? new Intl.NumberFormat('en-IN').format(field.value)
                                              : ''
                                        }
                                        onFocus={() => setIsEditing(true)}
                                        onBlur={(e) => {
                                          setIsEditing(false);
                                          let rawValue = e.target.value.replace(/,/g, '');
                                          let numValue = parseInt(rawValue, 10);

                                          if (!isNaN(numValue)) {
                                            field.onChange(numValue);
                                          }
                                        }}
                                        onChange={(e) => {
                                          let rawValue = e.target.value.replace(/,/g, '');
                                          if (/^\d*$/.test(rawValue)) {
                                            field.onChange(rawValue);
                                          }
                                        }}
                                        InputProps={{
                                          startAdornment: (
                                            field?.value ? (
                                              <InputAdornment position="start">
                                                <CurrencyRupeeIcon fontSize="small" />
                                              </InputAdornment>) : null
                                          ),
                                        }}
                                      />
                                    );
                                  }}
                                />
                              </FormControl>
                              <Controller
                                name="is_compensation_confidential"
                                control={control}
                                render={({ field }) => (
                                  <FormControlLabel
                                    control={
                                      <BpCheckbox
                                        {...field}
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                      />
                                    }
                                    label="Keep compensation confidential"
                                    sx={{
                                      '& .MuiFormControlLabel-label': {
                                        fontWeight: 'normal',
                                        color: 'rgba(0, 0, 0, 0.54)',
                                      },
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Required Expertise / Skills */}
                  <Box mb={4} className="job-posting-skills">
                    <Box className="card-container">
                      <Box className="card-title">
                        <Stack>
                          <Typography className="title-text">Required Expertise/ Skills</Typography>
                          <Typography className="info-text">Please add 5-10 relevant skills. You may indicate up to 5 preferred skills that are highly recommended. (Click on {' '}
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}><StarIcon sx={{ color: '#faaf00', fontSize: 'inherit' }} /></span>
                            {' '}  to indicate a preferred skill)</Typography>
                        </Stack>
                      </Box>

                      <Box className="card-body" >
                        {/* skills */}
                        <Stack direction="row" flexWrap="wrap" >
                          {chipData?.map((label, index) => (
                            <Box className="chip_container" sx={{ height: '40px!important' }}>
                              <Chip
                                key={index}
                                label={
                                  selectedChips?.includes(label) ? <span style={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton aria-label="delete" onClick={(e) => { e.stopPropagation(); handleCoreSkills(label); }}>
                                      <StarIcon sx={{ fill: coreSkill?.includes(label) ? '#faaf00 !important' : 'white !important' }} />
                                    </IconButton>
                                    {label}
                                  </span> : label
                                }
                                size="small"
                                clickable
                                variant={selectedChips?.includes(label) ? 'filled' : 'outlined'}
                                style={{
                                  color: selectedChips?.includes(label) ? 'white' : 'black',
                                  backgroundColor: selectedChips?.includes(label) ? '#1575C6' : '',
                                }}
                                onClick={() => handleClick(label)}
                              />
                            </Box>
                          ))}
                        </Stack>

                        {showFlag && (
                          <Typography style={{ color: 'red', fontSize: 'small' }}>
                            {showFlag}
                          </Typography>
                        )}
                        <Box sx={{ padding: '4px 0px' }}>
                          <Link className="add-more-skills-link" size="small" onClick={handleOpenOtpDialog} underline="none">
                            Add More Skills
                          </Link>
                        </Box>
                        <Dialog
                          open={open}
                          onClose={handleClose}
                          aria-labelledby="alert-dialog-title"
                          aria-describedby="alert-dialog-description"
                          className="otp-dialog-wrapper"
                        >
                          <AddSkills
                            onClose={handleClose}
                            skills={addMoreSkills}
                            selectedskills={selectedChips}
                            onAddSkills={handleAddMoreSkills}
                            type={'jobType'}
                          />
                        </Dialog>
                      </Box>
                    </Box>
                  </Box>

                  {/* Special Requirements */}
                  <Box mb={5}>
                    <Box className="card-container">
                      <Box className="card-title">
                        <Stack>
                          <Typography className="title-text">Special Requirements</Typography>
                          <Typography className="info-text">
                            Please mention if you are looking for specific candidate type
                          </Typography>
                        </Stack>
                      </Box>

                      <Box className="card-body">
                        <Grid container spacing={4}>
                          <Grid item xs={6}>
                            {/* gender preference */}
                            <FormControl fullWidth size="small" error={!!errors.gender_preference}>
                              <InputLabel size="small" id="gender_preference">
                                Gender preference
                              </InputLabel>
                              <Controller
                                name="gender_preference"
                                defaultValue=""
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    {...register('gender_preference')}
                                    size="small"
                                    label="Gender preference"
                                    variant="outlined"
                                    fullWidth>
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="Male">Male</MenuItem>
                                    <MenuItem value="Female">Female</MenuItem>
                                  </Select>
                                )}
                              />
                              {errors.gender_preference && (
                                <FormHelperText error>{errors.gender_preference?.message}</FormHelperText>
                              )}
                            </FormControl>
                          </Grid>

                          <Grid item xs={6}>
                            {/* disabilities preference */}
                            <FormControl fullWidth size="small" error={!!errors.disability_preference}>
                              <InputLabel id="">Disability Preference</InputLabel>
                              <Controller
                                control={control}
                                defaultValue=''
                                name="disability_preference"
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    {...register('disability_preference')}
                                    size="small"
                                    label="Disability preference"
                                    variant="outlined"
                                    fullWidth>
                                    <MenuItem value="Not applicable">Not applicable</MenuItem>
                                    <MenuItem value="Suitable for specially abled candidates only">Suitable for specially abled candidates only</MenuItem>
                                    <MenuItem value="Suitable for all candidates irrespective of any disabilities">Suitable for all candidates irrespective of any disabilities</MenuItem>
                                  </Select>
                                )}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                      <Box>
                        <Stack direction="row" spacing={2}>
                          <Button
                            className="continue-btn"
                            variant="contained"
                            size="medium"
                            disabled={isSubmitting || posting}
                            type="submit"
                          >
                            {alignment === 'external-job-posting' ? posting ? 'posting...' : 'Done' :
                              'Continue'
                            }
                          </Button>
                          {jobData?.status !== 'active' &&
                            <Tooltip
                              title={isDraftEnabled ? '' : 'Please fill out the Company Details, Designation & Job Function to save as draft.'}
                              arrow placement='top' disableHoverListener={isDraftEnabled} >
                              <span>
                                <Button className="sad-btn" variant="outlined" size="medium" disabled={!isDraftEnabled} onClick={() => saveAsDraft()}
                                  sx={{
                                    '&.Mui-disabled': {
                                      backgroundColor: '#f5f5f5 !important',
                                      color: '#999  !important',
                                      borderColor: '#ccc  !important',
                                      cursor: 'not-allowed  !important',
                                    }
                                  }}
                                >
                                  Save As Draft
                                </Button>
                              </span>
                            </Tooltip>
                          }
                        </Stack>
                      </Box>
                      <Button className="reset-btn" size="medium" onClick={() => onReset()}>
                        Reset
                      </Button>
                    </Stack>
                  </Box>
                </FormProvider>
              </Box>}
          </Box>
        </Container >
      </Box >

      <Drawer open={openCUEducDrawer} anchor={'right'} onClose={() => { setOpenCUEducDrawer(false); setDrawerJobData(null); }}>
        <ScreeningQuestionDrawer
          onClose={() => {
            setOpenCUEducDrawer(false);
            setDrawerJobData(null);
          }}
          keyQuestions={keyQuestions}
          setJobQuestions={handleQuestions}
          jobData={drawerJobData}
          jobUniqueId={jobUniqueId}
          jobId={jobId}
          alignment={alignment}
        />
      </Drawer>
      <Dialog
        open={openCopyUrlDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="action-dialog-card external-list-dialog"
      > <DialogContent>
          <CopyUrlDialog onClose={() => handleCloseCopyUrlDialog()} title={jobData?.title} jobId={jobId} />
        </DialogContent>
      </Dialog>
    </Box >
  );
};

export default NewJobPosting;
